import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, useCallback } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, getFirestore, serverTimestamp, setDoc } from "firebase/firestore";
import { FaListAlt } from "react-icons/fa";
import {
  BookMarked,
  BookPlus,
  ArrowRight,
  ExternalLink,
  FileDown,
  LoaderCircle,
  MessageCircle,
  MousePointerClick,
  Nfc,
  PencilLine,
  ShieldCheck,
} from "lucide-react";
import { Card, Col, Form, FormGroup, Modal, Row } from "react-bootstrap";
import Swal from "sweetalert2";
import { createPracticalReadingSsoRedirect } from "../services/practicalReadingSso.service.js";
import EstimulacionNeuronal from "../routes/Components/EstimulacionNeuronal.jsx";
import NotificationsBell from "./Components/NotificationsBell.jsx";
import { PageContainer } from "../components/PageContainer";
import "./DashboardView.scss";
import "./EditProfileView.scss";

export default function DashboardView() {
  const [currentUser, setCurrentUser] = useState({});
  const [professorProfile, setProfessorProfile] = useState(null);
  const [isOpeningPracticalExercises, setIsOpeningPracticalExercises] = useState(false);
  const [isOpeningTeacherChat, setIsOpeningTeacherChat] = useState(false);
  const auth = getAuth();
  const db = getFirestore();
  const navigate = useNavigate();
  const location = useLocation();
  const retryAttemptedRef = useRef(false);

  const isMaster = currentUser?.isMaster === true;
  const isTeacher = currentUser?.teacher === true;
  const isStudent = !isMaster && !isTeacher;
  const canManageStudents = isMaster || isTeacher;
  const userName = currentUser?.nombre || currentUser?.name || "Usuario";

  useEffect(() => {
    if (!currentUser?.teacherId) {
      setProfessorProfile(null);
      return;
    }

    const professorRef = doc(db, "users", currentUser.teacherId);

    getDoc(professorRef)
      .then((docSnap) => {
        setProfessorProfile(docSnap.exists() ? docSnap.data() : null);
      })
      .catch((error) => {
        console.error("Error getting document:", error);
        setProfessorProfile(null);
      });
  }, [currentUser, db]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const userDocRef = doc(db, "users", user.uid);
        getDoc(userDocRef)
          .then((docSnap) => {
            if (docSnap.exists()) {
              setCurrentUser({ id: user.uid, ...docSnap.data() });
            } else {
              console.log("No such document!");
            }
          })
          .catch((error) => {
            console.log("Error getting document:", error);
          });
      } else {
        setCurrentUser({});
      }
    });

    return () => unsubscribe();
  }, [auth, db]);

  const getSsoErrorMessage = (error) => {
    if (typeof error?.message === "string" && error.message.trim()) {
      return error.message;
    }

    return "Verifica la configuracion del SSO e intentalo de nuevo.";
  };

  const handleOpenPracticalExercises = useCallback(async ({ suppressErrorAlert = false } = {}) => {
    if (isOpeningPracticalExercises) return;

    try {
      setIsOpeningPracticalExercises(true);
      const redirectUrl = await createPracticalReadingSsoRedirect();

      if (!redirectUrl) {
        throw new Error("No se recibio la URL de acceso para Ejercicios Practicos.");
      }

      window.location.assign(redirectUrl);
    } catch (error) {
      console.error("Error iniciando SSO con Ejercicios Practicos:", error);
      setIsOpeningPracticalExercises(false);

      if (!suppressErrorAlert) {
        await Swal.fire({
          icon: "error",
          title: "No se pudo abrir Ejercicios Practicos",
          text: getSsoErrorMessage(error),
          confirmButtonText: "Entendido",
        });
      }
    }
  }, [isOpeningPracticalExercises]);

  const handleOpenTeacherChat = async () => {
    const studentId = currentUser?.id;
    const teacherId = currentUser?.teacherId;

    if (!studentId || !teacherId || isOpeningTeacherChat) return;

    try {
      setIsOpeningTeacherChat(true);
      const conversationId = studentId > teacherId
        ? `${studentId}_${teacherId}`
        : `${teacherId}_${studentId}`;
      const conversationRef = doc(db, "conversations", conversationId);
      const conversationSnap = await getDoc(conversationRef);

      if (!conversationSnap.exists()) {
        await setDoc(conversationRef, {
          participants: [studentId, teacherId],
          timestamp: serverTimestamp(),
        });
      }

      navigate(`/chat?conversationId=${encodeURIComponent(conversationId)}`);
    } catch (error) {
      console.error("Error abriendo el chat con el profesor:", error);
      await Swal.fire({
        icon: "error",
        title: "No se pudo abrir el chat",
        text: "Intentalo de nuevo en unos segundos.",
        confirmButtonText: "Entendido",
      });
    } finally {
      setIsOpeningTeacherChat(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shouldRetrySso = params.get("ssoRetry") === "practical-reading";

    if (!shouldRetrySso) {
      retryAttemptedRef.current = false;
      return;
    }

    if (!currentUser?.id || retryAttemptedRef.current) {
      return;
    }

    retryAttemptedRef.current = true;
    params.delete("ssoRetry");
    const nextSearch = params.toString();

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true }
    );

    handleOpenPracticalExercises();
  }, [currentUser?.id, handleOpenPracticalExercises, location.pathname, location.search, navigate]);

  return (
    <PageContainer className="dashboard-view">
      <div className="glass-navbar dashboard-header mb-4">
        <span className="glass-navbar__title dashboard-header__title">
          {`Bienvenido de nuevo, ${userName}!`}
        </span>

        <div className="dashboard-header__actions">
          {isStudent && currentUser?.id && (
            <NotificationsBell
              userId={currentUser.id}
              isTeacher={false}
              onlyChat={true}
            />
          )}

          {isTeacher && currentUser?.id && (
            <NotificationsBell
              userId={currentUser.id}
              isTeacher={true}
              onlyChat={false}
            />
          )}
        </div>
      </div>

      <Row className="dashboard-grid">
        <Col xs={12} md={4} xl={3} className="mb-4 dashboard-sidebar">
          <Card className="option-card profile-card mb-4">
            <Card.Body>
              <button
                type="button"
                className="editProfile"
                onClick={() => navigate("/dashboard/profile")}
                aria-label="Editar perfil"
              >
                <PencilLine className="option-icon" />
              </button>

              <Form>
                <FormGroup className="mb-3 text-center">
                  <img
                    src={currentUser.profilePicture || "/img/default-profile.jpg"}
                    alt="Profile"
                    className="img-thumbnail rounded-circle profile-avatar"
                  />
                  <h2 className="card-title profile-name">{userName}</h2>
                  <p className="profile-username">@{currentUser.displayName || "perfil"}</p>
                  <span className="profile-role">
                    {isMaster ? "Master" : isTeacher ? "Profesor" : "Alumno"}
                  </span>
                </FormGroup>
              </Form>
            </Card.Body>
          </Card>

          {isStudent && (
            <Card className="option-card assigned-teacher-card mb-4 glass">
              <Card.Body>
                <div className="assigned-teacher-card__heading">Tu profesor asignado</div>
                <Form>
                  <FormGroup>
                    {professorProfile ? (
                      <div className="assigned-teacher">
                        <img
                          src={professorProfile?.profilePicture || "/img/default-profile.jpg"}
                          alt={`Foto de ${professorProfile.displayName || professorProfile.nombre || professorProfile.name || "tu profesor"}`}
                          className="img-thumbnail rounded-circle profile-avatar profile-avatar--sm"
                        />
                        <h2 className="assigned-teacher__name">
                          {professorProfile.displayName || professorProfile.nombre || professorProfile.name || "Profesor"}
                        </h2>
                        <p className="assigned-teacher__role">Profesor</p>
                        <button
                          type="button"
                          className="assigned-teacher__cta"
                          onClick={handleOpenTeacherChat}
                          disabled={isOpeningTeacherChat}
                          aria-label="Abrir el chat con tu profesor"
                        >
                          <MessageCircle size={18} aria-hidden="true" />
                          <span>{isOpeningTeacherChat ? "Abriendo chat..." : "Chatea ahora"}</span>
                          <ArrowRight size={18} aria-hidden="true" />
                        </button>
                      </div>
                    ) : (
                      <p className="empty-helper">Aun no tienes profesor asignado.</p>
                    )}
                  </FormGroup>
                </Form>
              </Card.Body>
            </Card>
          )}

          {canManageStudents && (
            <Card className="option-card management-card mb-4 glass">
              <Card.Body>
                <div className="management-card__title">
                  <FaListAlt />
                  <span>{isMaster ? "Gestion general" : "Seccion Profesor"}</span>
                </div>
                <p className="management-card__text">
                  {isMaster
                    ? "Administra usuarios, profesores y alumnos desde accesos directos."
                    : "Consulta tus alumnos asignados y sus registros."}
                </p>
                <div className="management-card__actions">
                  <Link to="/listado-alumnos" className="btn btn-primary">Listado de alumnos</Link>
                  {isMaster && (
                    <>
                      <Link to="/master" className="btn btn-outline-primary">
                        Panel Master
                      </Link>
                      <Link to="/dashboard/master/pendientes-ejercicios-practicos" className="btn btn-outline-primary">
                        Pendientes SSO
                      </Link>
                    </>
                  )}
                </div>
              </Card.Body>
            </Card>
          )}

          {isStudent && (
            <Card className="option-card neural-card glass">
              <Card.Body>
                <div className="card-title fw-bold text-center my-3">
                  Reproductor de estimulacion neuronal
                </div>
                <EstimulacionNeuronal compact />
                <Link to="/EstimulacionNeuronal" className="mt-4 btn btn-primary">Ir</Link>
              </Card.Body>
            </Card>
          )}
        </Col>

        <Col xs={12} md={8} xl={9} className="mb-4 dashboard-main">
          <Row className="align-items-stretch dashboard-actions">
            <Col xs={6} md={4} xl={3} className="mb-3">
              <Link to="/material-didactico" className="dashboard-action-link">
                <div className="card option-card glass h-100 justify-content-between">
                  <div className="card-body d-flex justify-content-center align-items-center flex-column text-center">
                    <FileDown className="btn-icon" />
                    <div className="card-title mt-3">Material para imprimir</div>
                  </div>
                </div>
              </Link>
            </Col>

            <Col xs={6} md={4} xl={3} className="mb-3">
              <Link to="/material-teorico" className="dashboard-action-link">
                <div className="card option-card glass h-100 justify-content-between">
                  <div className="card-body d-flex justify-content-center align-items-center flex-column text-center">
                    <BookMarked className="btn-icon" />
                    <div className="card-title mt-3">Material Teorico</div>
                  </div>
                </div>
              </Link>
            </Col>

            <Col xs={6} md={4} xl={3} className="mb-3">
              <Link to="/cuaderno-digital" className="dashboard-action-link">
                <div className="card option-card glass h-100 justify-content-between">
                  <div className="card-body d-flex justify-content-center align-items-center flex-column text-center">
                    <MousePointerClick className="btn-icon" />
                    <div className="card-title mt-3">Cuaderno Digital</div>
                  </div>
                </div>
              </Link>
            </Col>

            <Col xs={6} md={4} xl={3} className="mb-3">
              <button
                type="button"
                className="card option-card glass h-100 justify-content-between dashboard-action-button"
                onClick={() => handleOpenPracticalExercises()}
                disabled={isOpeningPracticalExercises}
              >
                <div className="card-body d-flex justify-content-center align-items-center flex-column text-center">
                  <ExternalLink className="btn-icon" />
                  <div className="card-title mt-3">Aplicación web</div>
                </div>
              </button>
            </Col>

            <Col xs={6} md={4} xl={3} className="mb-3">
              <Link to="/ViewLecturasAlumnos" className="dashboard-action-link">
                <div className="card option-card glass h-100 justify-content-between">
                  <div className="card-body d-flex justify-content-center align-items-center flex-column text-center">
                    <BookPlus className="btn-icon" />
                    <div className="card-title mt-3">Lecturas adicionales</div>
                  </div>
                </div>
              </Link>
            </Col>

            <Col xs={6} md={4} xl={3} className="mb-3">
              <Link to="/practicas-alumno" className="dashboard-action-link">
                <div className="card option-card glass h-100 justify-content-between">
                  <div className="card-body d-flex justify-content-center align-items-center flex-column text-center">
                    <Nfc className="btn-icon" />
                    <div className="card-title mt-3">Registro de practicas</div>
                  </div>
                </div>
              </Link>
            </Col>

            {isMaster && (
              <Col xs={6} md={4} xl={3} className="mb-3">
                <Link to="/dashboard/master/pendientes-ejercicios-practicos" className="dashboard-action-link">
                  <div className="card option-card glass h-100 justify-content-between">
                    <div className="card-body d-flex justify-content-center align-items-center flex-column text-center">
                      <ShieldCheck className="btn-icon" />
                      <div className="card-title mt-3">Pendientes SSO</div>
                    </div>
                  </div>
                </Link>
              </Col>
            )}
          </Row>
        </Col>
      </Row>

      <footer
        className="dashboard-mobile-footer"
        aria-label="Versión de la plataforma"
      >
        <span aria-hidden="true" />
        <strong>V2.2.2</strong>
        <span aria-hidden="true" />
      </footer>

      <Modal
        show={isOpeningPracticalExercises}
        backdrop="static"
        keyboard={false}
        centered
        dialogClassName="practical-auth-modal"
        contentClassName="practical-auth-modal__content"
        aria-labelledby="practical-auth-title"
        aria-describedby="practical-auth-description"
      >
        <Modal.Body>
          <div className="practical-auth-modal__indicator" aria-hidden="true">
            <LoaderCircle />
          </div>
          <p className="practical-auth-modal__eyebrow">Acceso seguro</p>
          <h2 id="practical-auth-title">Estamos autenticando tu cuenta</h2>
          <p id="practical-auth-description" aria-live="polite">
            Espera unos segundos. Te llevaremos automáticamente a Ejercicios Prácticos cuando el acceso esté listo.
          </p>
          <div className="practical-auth-modal__progress" aria-hidden="true">
            <span />
          </div>
          <p className="practical-auth-modal__note">No cierres ni recargues esta ventana.</p>
        </Modal.Body>
      </Modal>
    </PageContainer>
  );
}
